/**
 * EnterpriseEnquiryModal — public Enterprise enquiry form.
 * POSTs /api/public/enterprise-enquiry (no auth). Used by the landing page,
 * the in-app plan picker and the location-limit popup.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Building, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', organisation: '', phone: '', email: '', city: '', locations_count: '', users_count: '', message: '' };
const FIELDS = [
  ['name', 'Your name', 'text'], ['organisation', 'Organisation', 'text'],
  ['phone', 'Phone', 'tel'], ['email', 'Email', 'email'], ['city', 'City', 'text'],
  ['locations_count', 'Number of locations', 'number'], ['users_count', 'Number of users needed', 'number'],
];

export const EnterpriseEnquiryModal = ({ open, onClose, source = 'app', prefill }) => {
  const [f, setF] = useState({ ...EMPTY, ...(prefill || {}) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/public/enterprise-enquiry`, {
        ...f, locations_count: Number(f.locations_count), users_count: Number(f.users_count), source,
      });
      setDone(true);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(Array.isArray(d) ? `Please check: ${d.map((x) => x.loc?.slice(-1)[0]).join(', ')}` : (d || 'Could not send your enquiry'));
    } finally { setBusy(false); }
  };
  const close = () => { setDone(false); setErr(''); setF({ ...EMPTY, ...(prefill || {}) }); onClose(); };
  const input = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-slate-900 transition-colors';

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center bg-slate-900/60 p-4" data-testid="enterprise-enquiry-modal" data-readonly-safe="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-auto p-6 relative text-slate-900">
        <button onClick={close} className="absolute right-3 top-3 text-slate-400 hover:text-slate-700" data-testid="enterprise-enquiry-close" aria-label="Close"><X size={16} /></button>
        <h3 className="text-base font-bold flex items-center gap-1.5"><Building size={16} /> Talk to us about Enterprise</h3>
        <p className="text-[12px] text-slate-500 mt-0.5">For 4+ locations or hospitals. We'll get back within one working day.</p>
        {done ? (
          <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-start gap-2" data-testid="enterprise-enquiry-success">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> Thank you — our team will contact you within 24 hours.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <label key={k} className={k === 'organisation' || k === 'email' ? 'col-span-2 sm:col-span-1 block' : 'block'}>
                <span className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</span>
                <input required type={type} min={type === 'number' ? 1 : undefined} value={f[k]}
                  onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`enterprise-enquiry-${k}`} className={input} />
              </label>
            ))}
            <label className="col-span-2 block">
              <span className="block text-[11px] font-semibold text-slate-600 mb-1">Message</span>
              <textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
                data-testid="enterprise-enquiry-message" className={input} />
            </label>
            {err && <div className="col-span-2 text-xs rounded bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 flex items-center gap-1.5" data-testid="enterprise-enquiry-error"><AlertTriangle size={12} />{err}</div>}
            <button type="submit" disabled={busy} data-testid="enterprise-enquiry-submit"
              className="col-span-2 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
              {busy ? 'Sending…' : 'Send enquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
